// ═══════════════════════════════════════════════════════════════════════════
//  ΝΑΥΛΩΤΗΣ: ΦΥΣΙΚΟ ΠΡΟΣΩΠΟ Ή ΕΤΑΙΡΕΙΑ — ΜΙΑ ΠΗΓΗ ΓΙΑ ΤΟΝ ΚΑΝΟΝΑ
//  ---------------------------------------------------------------------------
//  Η εταιρεία ΔΕΝ έχει φύλο, ημερομηνία γέννησης, εθνικότητα ή διαβατήριο — και
//  ΔΕΝ ταξιδεύει. Ως τώρα η φόρμα ζητούσε τα ατομικά από όλους και το save
//  πρόσθετε τον ναυλωτή στο crew list ως επιβάτη· με εταιρικό ναυλωτή έβγαινε
//  ένας ανύπαρκτος επιβάτης «ACME LTD» στη λίστα και στην ΑΑΔΕ.
//
//  ΤΟ ΟΝΟΜΑ: η επωνυμία γράφεται ΚΑΙ στο `lastName`/`name` του ναυλωτή, ώστε
//  κάθε υπάρχων αναγνώστης (DOCX, emails, λίστες, ΑΑΔΕ fallback) να δείχνει την
//  εταιρεία χωρίς να ξέρει τίποτα για το νέο πεδίο.
// ═══════════════════════════════════════════════════════════════════════════

/** Τα ΑΤΟΜΙΚΑ πεδία — κρύβονται και δεν αποθηκεύονται για εταιρεία. */
export const INDIVIDUAL_KEYS = ['dob', 'dateOfBirth', 'gender', 'nationality',
                                'idType', 'idNumber', 'passport'] as const;

/** Τα flat αντίστοιχα του ναύλου (booking_data). */
export const INDIVIDUAL_FLAT_KEYS = ['chartererDob', 'chartererGender', 'chartererNationality',
                                     'chartererIdType', 'chartererIdNumber'] as const;

const s = (v: any) => String(v ?? '').trim();

/**
 * Εταιρικός ναυλωτής; Δέχεται ΚΑΙ το nested `chartererData` ΚΑΙ το flat
 * `booking_data` — τα δύο σχήματα ζουν μαζί σε όλο το app.
 */
export const isCompanyCharterer = (src: any): boolean => {
  if (!src || typeof src !== 'object') return false;
  /* Ο server καθρεφτίζει boolean → '1'/'true' (JSONB + cm_str). Δεχόμαστε και
     τα δύο· αλλιώς ένας εταιρικός ναύλος γύριζε «φυσικό πρόσωπο» στο load. */
  const truthy = (v: any) => v === true || v === 1 || ['1', 'true'].includes(s(v).toLowerCase());
  if (truthy(src.isCompany) || truthy(src.chartererIsCompany)) return true;
  const t = s(src.chartererType || src.partyType || src.party_type).toLowerCase();
  return t === 'company' || t === 'legal';
};

/** Η επωνυμία, από όποιο σχήμα κι αν έρχεται. */
export const companyNameOf = (src: any): string =>
  s(src?.companyName) || s(src?.chartererCompanyName) ||
  (isCompanyCharterer(src) ? (s(src?.lastName) || s(src?.chartererLastName) || s(src?.name)) : '');

/** Τι γράφεται ως όνομα ναυλωτή: επωνυμία ή «Όνομα Επώνυμο». */
export const chartererDisplayName = (src: any): string => {
  if (isCompanyCharterer(src)) return companyNameOf(src);
  const f = s(src?.firstName) || s(src?.chartererFirstName);
  const l = s(src?.lastName) || s(src?.chartererLastName);
  return `${f} ${l}`.trim() || s(src?.name) || s(src?.chartererName);
};

/** Καθαρίζει τα ατομικά πεδία — για εταιρεία δεν ταξιδεύουν πουθενά. */
export function stripIndividualFields<T extends Record<string, any>>(cd: T): T {
  const out: any = { ...cd };
  INDIVIDUAL_KEYS.forEach(k => { delete out[k]; });
  INDIVIDUAL_FLAT_KEYS.forEach(k => { delete out[k]; });
  return out;
}

/**
 * Η λίστα πληρώματος ΧΩΡΙΣ τη γραμμή του ναυλωτή.
 * Μια εταιρεία δεν επιβαίνει: ούτε ως επιβάτης, ούτε στην ΑΑΔΕ.
 */
export const crewWithoutCharterer = (crew: any[]): any[] =>
  (Array.isArray(crew) ? crew : []).filter((m: any) => !(m && m.isCharterer === true));

/**
 * ΤΑ ΠΕΔΙΑ ΠΟΥ ΜΕΝΟΥΝ ΓΙΑ ΕΤΑΙΡΕΙΑ: επωνυμία, ΑΦΜ/VAT, ΔΟΥ, διεύθυνση, email,
 * τηλέφωνο, χώρα. Ό,τι λείπει είναι ΕΝΔΕΙΞΗ, ποτέ φραγή — ίδια φιλοσοφία με τον
 * ναυλωτή-φυσικό πρόσωπο (το submit δεν μπλοκάρει από το v6.464).
 */
export const COMPANY_FIELDS = ['companyName', 'taxNumber', 'taxOffice', 'address', 'email', 'tel', 'country'] as const;

export const companyMissing = (cd: any): string[] => {
  const miss: string[] = [];
  if (!companyNameOf(cd)) miss.push('επωνυμία');
  if (!s(cd?.taxNumber) && !s(cd?.chartererTaxNumber)) miss.push('ΑΦΜ/VAT');
  return miss;
};

/**
 * Ό,τι γράφεται στον ναύλο για εταιρικό ναυλωτή: η επωνυμία μπαίνει και στα
 * παλιά πεδία ονόματος, τα ατομικά φεύγουν, το firstName αδειάζει ρητά.
 */
export function companyChartererPatch(companyName: string, country = ''): Record<string, any> {
  const n = s(companyName);
  return {
    isCompany: true,
    companyName: n,
    firstName: '',
    lastName: n,
    name: n,
    ...(s(country) ? { country: s(country) } : {}),
  };
}
